import { 
    useContext,
    useEffect, 
    useState 
} from 'react';
import { useRouter } from "next/router";

import { Groups } from '@mui/icons-material';
import { 
    Button,
    FormControlLabel,
    MenuItem, 
    Radio, 
    RadioGroup, 
    TextField 
} from '@mui/material';

import { RequestNextApi } from '../../api/Request';
import { ShowNotification } from '../../utils/notification/snackbarUtils';
import { MembershipJoinContext } from '../Context/membershipJoinContext';
import { ProgressContext } from '../Context/progressContext';
import { StyledCheckbox } from '../StyledCheckbox/StyledCheckbox';

const years = (startYear) => {
    const max = new Date().getFullYear();
    const min = max - 4;
    const years = [];

    for (let i = max; i >= min; i--) {
        years.push(i);
    }
    return years;
};

export default function MembershipType() {
    const { state: progressContext, update: updateProgress} = useContext(ProgressContext);
    const { state: membershipInfoContext, update: updateMembershipInfo} = useContext(MembershipJoinContext);

    const [type, setType] = useState();
    const [categories, setCategories] = useState();
    
    const router = useRouter();

    
    useEffect(() => {
        const fetchMembershipCategories = async () => {
            updateProgress({isLoading: true});
            try {
                const result = await RequestNextApi.post('/global-variables',
                    {}
                );
                if (result.data.success) {
                    setCategories(result.data.globals.membershipCategories);
                } else {
                    ShowNotification.error(result.data.message);
                }
            } catch (error) {
                ShowNotification.error('Failed fetching membership types');
            }
            updateProgress({isLoading: false});
        };
        fetchMembershipCategories();
    }, []);

    const updateMembershipType = (e) => {
        setType(e.target.value);

        updateMembershipInfo({
            ...membershipInfoContext,
            user: {
                ...membershipInfoContext.user,
                memberType: e.target.value,
                membershipFrequency: 'year',
                st_school: '',
                st_graduation: '',
                st_degree_of_interest: '',
                st_enrolled_in_cfp: e.target.value === 'st' ? false : undefined,
                fa_school: '',
                fa_courses: ''
            },
            sale: {
                ...membershipInfoContext.sale,
                sku: categories[e.target.value]?.sku.default,
                itemPrice: categories[e.target.value]?.price.year,
                totalPrice: categories[e.target.value]?.price.year
            },
            profile: {
                ...membershipInfoContext.profile,
                credentials: [],
                yearStartedIndustry: '',
            }
        });
    };

    const updateMembershipFrequency = (e) => {
        const sku = type !== 'pr'
            ? categories[type].sku.default
            : categories[type].sku[e.target.value] ?? categories[type].sku.default;
        const itemPrice = categories[type].price[e.target.value];
        const totalPrice = categories[type].price[e.target.value];
        updateMembershipInfo({
            ...membershipInfoContext,
            user: {
                ...membershipInfoContext.user,
                membershipFrequency: e.target.value,
            },
            sale: {
                ...membershipInfoContext.sale,
                sku: sku,
                itemPrice: itemPrice,
                totalPrice: totalPrice
            }
        });
    };

    const updateCredentials = (e) => {
        if (e.target.checked === true) {
            let newCredentials = [...membershipInfoContext?.profile?.credentials ?? [], e.target.name];
            updateMembershipInfo({
                ...membershipInfoContext,
                profile: {
                    ...membershipInfoContext.profile,
                    credentials: newCredentials
                }
            });
        } else if (e.target.checked === false) {
            updateMembershipInfo({
                ...membershipInfoContext,
                profile: {
                    ...membershipInfoContext.profile,
                    credentials: membershipInfoContext?.profile?.credentials?.filter(credential => credential !== e.target?.name)
                }
            });
        }
    };

    return (
        <div className='membership-type my-11'>
            <div className='flex space-x-2'>
                <Groups className='text-blue-900' fontSize='large' /> 
                <div className='text-3xl text-blue-900'>Membership Type and Payment Plan</div>
            </div>
            {categories && (
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mt-8'>
                    <TextField
                        label="Membership Type" 
                        color="third" 
                        fullWidth 
                        id="employment"
                        name="employment"
                        placeholder=""
                        focused
                        select
                        required
                        size='small'
                        value={type}
                        onChange={updateMembershipType}
                    >
                        {Object.entries(categories)?.map(([key, value]) => {
                            if (value.private === false && key !== 'do') {
                                return (
                                    <MenuItem key={key} value={key}>
                                        {value.type}
                                    </MenuItem>
                                )
                            }
                        })}
                    </TextField>
                    <div className='md:px-3'>
                        {type && (
                            <div className=''>
                                <div className='text-xl text-center'>
                                    {categories[type]?.legend}
                                </div>
                                {type !== 'pr' && (
                                    <div className='mt-5 text-2xl flex justify-center'>
                                        <RadioGroup
                                            row
                                            aria-labelledby="demo-row-radio-buttons-group-label"
                                            name="membershipFrequency"
                                            value={membershipInfoContext?.user?.membershipFrequency}
                                            defaultValue={'year'}
                                            onChange={updateMembershipFrequency}
                                        >
                                            {categories[type].price.quarter && (
                                                <FormControlLabel 
                                                    value={'quarter'} 
                                                    control={<Radio color="third" required={true} />} 
                                                    label={`$${categories[type].price.quarter}/quarter`}
                                                    className='text-gray-600'
                                                />
                                            )}
                                            {categories[type].price.year && (
                                                <FormControlLabel 
                                                    value={'year'} 
                                                    control={<Radio color="third" required={true} />}
                                                    label={`$${categories[type].price.year}/year`}
                                                    className='text-gray-600'
                                                />
                                            )}
                                        </RadioGroup>
                                    </div>
                                )}
                                {type === 'pr' && (
                                    <div className='mt-5'>
                                        <div className='mt-2 text-2xl'>
                                            <RadioGroup
                                                col
                                                aria-labelledby="demo-row-radio-buttons-group-label"
                                                name="membershipFrequency"
                                                value={membershipInfoContext?.user?.membershipFrequency}
                                                defaultValue={'year'}
                                                onChange={updateMembershipFrequency}
                                            >
                                                <div className='text-xl font-bold'>Membership Only</div>
                                                <div className='mt-2 flex justify-center'>
                                                    <FormControlLabel 
                                                        value={'quarter'} 
                                                        control={<Radio color="third" required={true} />} 
                                                        label={`$${categories[type].price.quarter}/quarter`}
                                                        className='text-gray-600'
                                                    />
                                                    <FormControlLabel 
                                                        value={'year'} 
                                                        control={<Radio color="third" required={true} />}
                                                        label={`$${categories[type].price.year}/year`}
                                                        className='text-gray-600'
                                                    />
                                                </div>
                                                <div className='text-xl font-bold mt-2'>Membership and Training</div>
                                                <div className='mt-2'>
                                                    <FormControlLabel 
                                                        value={'bundleEdu'} 
                                                        control={<Radio color="third" required={true} />} 
                                                        label={`$${categories[type].price.bundleEdu}/One year of Membership and CKA® Educational Program`}
                                                        className='text-gray-600'
                                                    />
                                                    <FormControlLabel 
                                                        value={'bundleCanada'} 
                                                        control={<Radio color="third" required={true} />}
                                                        label={`$${categories[type].price.bundleCanada}/One year of Membership and Kingdom Advisors Core Training - Canadian Resident Special`}
                                                        className='text-gray-600'
                                                    />
                                                    <FormControlLabel 
                                                        value={'bundle'} 
                                                        control={<Radio color="third" required={true} />}
                                                        label={`$${categories[type].price.bundle}/One year of Membership and Kingdom Advisors Core Training for personal enrichment`}
                                                        className='text-gray-600'
                                                    />
                                                </div>
                                            </RadioGroup>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {type === 'pr' && (
                            <div className='mt-5'>
                                <div className='text-xl'>
                                    Which of these certifications do you hold? (Check all that apply)
                                </div>
                                <div className='grid grid-cols-2 gap-3'>
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CFA®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CFA®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CFP®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CFP®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="ChFC®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="ChFC®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CIMA®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CIMA®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CLU®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CLU®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CPA"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CPA"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CPA/PFS"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CPA/PFS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="EA"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="EA"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="AAMS"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="AAMS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="JD"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="JD"
                                    />
                                </div>
                            </div>
                        )}
                        {type === 'em' && (
                            <div className='mt-5'>
                                <TextField
                                    label="In what year did you enter the financial services industry full-time?" 
                                    color="third" 
                                    fullWidth 
                                    id="employment"
                                    name="employment"
                                    placeholder=""
                                    focused
                                    select
                                    required
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            profile: {
                                                ...membershipInfoContext.profile,
                                                yearStartedIndustry: e.target.value
                                            }
                                        });
                                    }}
                                >
                                    {years(1990).map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </div>
                        )}
                        {type === 'st' && (
                            <div className='mt-5 space-y-5'>
                                <TextField
                                    label="Which college or university do you attend?" 
                                    color="third" 
                                    fullWidth 
                                    id="st_school"
                                    name="st_school"
                                    placeholder="Texas A&M University"
                                    focused
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            user: {
                                                ...membershipInfoContext.user,
                                                st_school: e.target.value
                                            }
                                        })
                                    }}
                                    required
                                />
                                <TextField
                                    label="What is your anticipated graduation date (MM/YYYY)?" 
                                    color="third" 
                                    fullWidth 
                                    id="st_graduation"
                                    name="st_graduation"
                                    placeholder="05/2026"
                                    focused
                                    required
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            user: {
                                                ...membershipInfoContext.user,
                                                st_graduation: e.target.value
                                            }
                                        })
                                    }}
                                />
                                <TextField
                                    label="What degree are you pursuing?"
                                    color="third" 
                                    fullWidth 
                                    id="st_degree_of_interest"
                                    name="st_degree_of_interest"
                                    placeholder="Bachelor's"
                                    focused
                                    required
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            user: {
                                                ...membershipInfoContext.user,
                                                st_degree_of_interest: e.target.value
                                            }
                                        })
                                    }}
                                />
                                <div>
                                    <div className='text-xl'>
                                        Are you enrolled in a board-registered CFP® educational program?
                                    </div>
                                    <RadioGroup
                                        row
                                        aria-labelledby="demo-row-radio-buttons-group-label"
                                        name="conferenceTeam"
                                        defaultValue={false}
                                        onChange={(e) => {
                                            updateMembershipInfo({
                                                ...membershipInfoContext,
                                                user: {
                                                    ...membershipInfoContext.user,
                                                    st_enrolled_in_cfp: e.target.value
                                                }
                                            })
                                        }}
                                        className='flex'
                                    >
                                        <FormControlLabel value={true} control={<Radio color="third" required={true} />} label='Yes' />
                                        <FormControlLabel value={false} control={<Radio color="third" required={true} />} label='No' />
                                    </RadioGroup>
                                </div>
                            </div>
                        )}
                        {type === 'fa' && (
                            <div className='mt-5 space-y-5'>
                                <TextField
                                    label="Which college or university employs you?" 
                                    color="third" 
                                    fullWidth 
                                    id="fa_school"
                                    name="fa_school"
                                    placeholder="Texas A&M University"
                                    focused
                                    required
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            user: {
                                                ...membershipInfoContext.user,
                                                fa_school: e.target.value
                                            }
                                        })
                                    }}
                                />
                                <TextField
                                    label="What courses do you teach?" 
                                    color="third" 
                                    fullWidth 
                                    id="fa_courses"
                                    name="fa_courses"
                                    placeholder="Financial Planning"
                                    focused
                                    required
                                    size='small'
                                    onChange={(e) => {
                                        updateMembershipInfo({
                                            ...membershipInfoContext,
                                            user: {
                                                ...membershipInfoContext.user,
                                                fa_courses: e.target.value
                                            }
                                        })
                                    }}
                                />
                                <div className='text-xl'>
                                    Which of these certifications do you hold? (Check all that apply)
                                </div>
                                <div className='grid grid-cols-2 gap-3'>
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CFA®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CFA®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CFP®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CFP®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="ChFC®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="ChFC®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CIMA®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CIMA®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CLU®"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CLU®"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CPA"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CPA"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="CPA/PFS"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="CPA/PFS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="EA"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="EA"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="AAMS"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="AAMS"
                                    />
                                    <FormControlLabel
                                        control={
                                            <StyledCheckbox
                                                name="JD"
                                                onChange={updateCredentials}
                                            />
                                        }
                                        label="JD"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <div className='mt-10'>
                <div className='text-xl font-bold mt-5 text-center'>
                    Kingdom Advisors Membership Attestation
                </div>
                <div className='text-xl mt-5 text-center'>
                    The members of Kingdom Advisors support the vision and mission of KA, advocating for the Christian financial industry, advancing biblical financial advice, and upholding the Certified Kingdom Advisor® designation as its standard of excellence. I understand that KA may terminate the membership of any individual whose conduct undermines the KA vision or mission or harms the reputation of KA or its members.
                </div>
                <div className='text-center mt-5'>
                    <FormControlLabel
                        control={
                            <StyledCheckbox
                                id='agreeCheck'
                                name="agreeCheck"
                                required
                                onInvalidCapture={() => {
                                    document
                                        .getElementById('agreeCheck')
                                        .setCustomValidity('Please review and agree to the Membership Attestation to proceed.')
                                }}
                                onChange={() => {
                                    document
                                        .getElementById('agreeCheck')
                                        .setCustomValidity('')
                                }}
                            />
                        }
                        label={<div className='italic'>I have read, understand, and agree with the above membership attestation.</div>}
                    />
                </div>
                <div className='text-center'>
                    <Button 
                        variant="contained" 
                        className='bg-secondary-500 hover:bg-secondary-500'
                        type='submit'
                    >
                        Proceed to Payment
                    </Button>
                </div>
            </div>
        </div>
    )
}
